import type { FC, ReactNode } from 'react';

type VerticalPosition = 'center' | 'bottom';
type HorizontalPosition = 'start' | 'end';

interface FloatingPanelProps {
  side: HorizontalPosition;
  position?: VerticalPosition;
  children: ReactNode;
}

/**
 * Fixed-position panel constrained to the max-w-5xl content column.
 * Renders children as a vertical stack at the left or right content edge.
 *
 * - `side="start"` → left-aligned within content bounds
 * - `side="end"` → right-aligned within content bounds
 * - `position="center"` (default) → vertically centered below the header
 * - `position="bottom"` → anchored to the bottom with safe-area inset
 */
export const FloatingPanel: FC<FloatingPanelProps> = ({
  side,
  position = 'center',
  children,
}) => {
  const positionStyle = position === 'center'
    ? { top: 'calc(50% + 1.75rem)', transform: 'translateY(-50%)' }
    : { bottom: 0, paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' };

  return (
    <div
      className="fixed left-0 right-0 z-50 pointer-events-none"
      style={positionStyle}
    >
      <div className={`max-w-5xl mx-auto px-4 flex ${side === 'end' ? 'justify-end' : 'justify-start'}`}>
        <div className="pointer-events-auto flex flex-col items-end gap-1">
          {children}
        </div>
      </div>
    </div>
  );
};
